import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { Typography } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import { StyledListItem } from "core/components/StyledListItem";
import { useBreakpoints } from "core/hooks/useBreakpoints";
import type { ReactNode } from "react";

interface SearchItemTemplateProps {
  title: ReactNode;
  titleSecondary?: ReactNode;
  dateLabel?: string;
  isFavorited: boolean;
  dateText?: string;
  AvatarIcon: ReactNode;
  middleSlot?: ReactNode;
  secondaryAction: ReactNode;
  "data-testid": string;
}

export function SearchItemTemplate({
  title,
  titleSecondary: blurb,
  dateLabel,
  dateText,
  AvatarIcon,
  middleSlot,
  secondaryAction,
  isFavorited,
  "data-testid": testId,
}: SearchItemTemplateProps) {
  const { isMobile } = useBreakpoints();
  if (isMobile) {
    return (
      <StyledListItem
        data-testid={testId}
        data-component="search-item"
        sx={{ columnGap: 2, paddingRight: 10 }}
        secondaryAction={<Box color="primary.main">{secondaryAction}</Box>}
      >
        <ListItemAvatar sx={{ minWidth: "auto" }}>
          <Avatar
            sx={{
              backgroundColor: "primary.main",
              color: "primary.contrastText",
            }}
          >
            {AvatarIcon}
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          sx={{
            flex: 1,
            "& .MuiListItemText-secondary": {
              maxWidth: "40ch",
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 3,
            },
          }}
          primary={title}
          secondary={
            <>
              {dateText}
              {dateText && <br />}
              {blurb}
            </>
          }
        />

        {isFavorited ? (
          <StarIcon color="primary" />
        ) : (
          <StarBorderIcon color="primary" />
        )}
      </StyledListItem>
    );
  }
  return (
    <StyledListItem
      data-testid={testId}
      data-component="search-item"
      sx={{ columnGap: 2, paddingRight: 10 }}
      secondaryAction={secondaryAction}
    >
      <ListItemAvatar sx={{ minWidth: "auto" }}>
        <Avatar
          sx={{
            backgroundColor: "primary.main",
            color: "primary.contrastText",
          }}
        >
          {AvatarIcon}
        </Avatar>
      </ListItemAvatar>
      <Box
        flex={1}
        gap={2}
        alignItems="center"
        display="grid"
        gridTemplateColumns="auto 1fr 1fr auto"
      >
        {isFavorited ? (
          <StarIcon color="primary" />
        ) : (
          <StarBorderIcon color="primary" />
        )}
        <ListItemText
          sx={{
            flex: 1,
            "& .MuiListItemText-secondary": {
              maxWidth: "40ch",
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            },
          }}
          primary={title}
          secondary={blurb}
        />
        {typeof middleSlot === "string" ? (
          <Typography variant="body2" color="textSecondary">
            {middleSlot}
          </Typography>
        ) : middleSlot != null ? (
          <div>{middleSlot}</div>
        ) : (
          <div />
        )}
        {dateText && dateLabel ? (
          <ListItemText
            sx={{ flex: 1 }}
            primary={dateLabel}
            slotProps={{
              primary: { variant: "subtitle2", textAlign: "right" },
              secondary: { textAlign: "right" },
            }}
            secondary={dateText}
          />
        ) : (
          <div />
        )}
      </Box>
    </StyledListItem>
  );
}
